import { boxedLabel } from "./boxedLabelConfiguration.js";
import {
	GRAPH_DEFAULTS,
	GRAPH_TYPES,
	LABEL_VALUES,
	SCALES,
} from "../Enums/index.js";
import { formatNumberForBar } from "../Scales/scaleNumberFormatter.js";
import isAtTick from "./isAtTick.js";
import DocumentDO = App.Data.Explorer.DocumentDO;
import { labelIntersectsWithXLine } from "./utils.js";

const isProgrammaticCapacity = (ctx): boolean =>
	ctx.dataset.label === "capacity_programmatic";

export const residualCapacityLabel = (
	derivedDocument: DocumentDO,
	isDarkMode: boolean
) => {
	return {
		formatter: (value, ctx) => {
			const meta = ctx.chart.getDatasetMeta(ctx.datasetIndex);
			const model = meta.data[ctx.dataIndex];
			const { width } = model.getProps(["width"], true);

			const labelValue = derivedDocument.is_visualised_as_percentage_share
				? LABEL_VALUES.PERCENTAGE
				: derivedDocument.label_value;
			const chart = ctx.chart;
			const stackTotal =
				chart.$totalizer.stacktotal[SCALES.Y][
					`${ctx.dataset.stack}-${ctx.dataset.mirrorDirection}-${ctx.dataset.motivesType}`
				]?.[ctx.dataIndex];

			if (stackTotal === undefined || stackTotal === null) {
				return null;
			}

			// important guard
			if (ctx.dataset.yAxisID === SCALES.Y_CAPACITY) {
				// Programmatic capacity is stacked on top of normal capacity and shows its own share instead of a residual.
				const labelAmount: number = isProgrammaticCapacity(ctx)
					? value
					: chart.$totalizer.stacktotal[SCALES.Y][
							`${ctx.dataset.stack}-${ctx.dataset.mirrorDirection}-residualCapacity`
						][ctx.dataIndex];

				switch (labelValue) {
					case LABEL_VALUES.CATEGORY: {
						return null; // not relevant for resCap.
					}
					case LABEL_VALUES.PERCENTAGE: {
						return formatNumberForBar(
							"percentage",
							derivedDocument.label_scale_format,
							labelAmount / stackTotal,
							width
						);
					}
					case LABEL_VALUES.PERCENTAGE_OF_TOTAL: {
						const total =
							chart.$totalizer[`graphYTotal_${ctx.dataset.mirrorDirection}`] +
							(chart.$totalizer[`graphYResidualCapacityTotal`] ?? 0);
						return formatNumberForBar(
							"percentage",
							derivedDocument.label_scale_format,
							labelAmount / total,
							width
						);
					}
					default: {
						return formatNumberForBar(
							derivedDocument.formatForGraph!,
							derivedDocument.label_scale_format,
							labelAmount,
							width
						);
					}
				}
			}
			return null;
		},
		align: (ctx) => {
			return isProgrammaticCapacity(ctx) ? "center" : "start";
		},
		anchor: (ctx) => {
			return isProgrammaticCapacity(ctx) ? "center" : "end"; // : "start";
		},
		offset: (ctx) => {
			if (isProgrammaticCapacity(ctx)) {
				return 0;
			}

			const chart = ctx.chart;
			const element = chart.getDatasetMeta(ctx.datasetIndex).data[
				ctx.dataIndex
			];
			const capacityTopY = element.y;

			const residualCapacityKey = `${ctx.dataset.stack}-${ctx.dataset.mirrorDirection}-residualCapacity`;
			const residualCapacity =
				chart.$totalizer.stacktotal[SCALES.Y]?.[residualCapacityKey]?.[
					ctx.dataIndex
				] ?? 0;
			const actuals =
				(ctx.dataset.data[ctx.dataIndex] as number) - residualCapacity;
			const stackedTopY = chart.scales[SCALES.Y].getPixelForValue(actuals);

			return Math.max(0, (stackedTopY - capacityTopY) / 2 - 7);
		},
		...boxedLabel(derivedDocument, isDarkMode),
		font: function () {
			return {
				size: 12,
				lineHeight: 1,
			};
		},
		color: function () {
			return isDarkMode
				? GRAPH_DEFAULTS.TEXT_COLOR_DARK
				: GRAPH_DEFAULTS.TEXT_COLOR;
		},
		display: function (ctx) {
			if (
				!(
					derivedDocument.is_label_visible &&
					ctx.dataset.yAxisID === SCALES.Y_CAPACITY &&
					derivedDocument.graph_type !== GRAPH_TYPES.LINE &&
					isAtTick(ctx)
				)
			) {
				return false;
			}

			return labelIntersectsWithXLine(ctx);
		},
	};
};
